import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { LoginService } from 'src/app/pages/login/services/login.service';
import { DocumentoPdfConfiguracionApiService } from 'src/app/pages/configuracion/Envios/documentos/services/documento-pdf-configuracion-api.service';
import { ConversionDatosApiService } from 'src/app/shared/services/conversion-datos-api.service';
import { EndpointService } from 'src/app/shared/services/endpoint.service';
import { EndpointConfigV3 } from 'src/environments/Configuration/EnpointConfig.v3';
import { environment } from 'src/environments/environment';
import { EnvioSimpleConfirmacionResultado } from '../models/envio-simple-confirmacion.model';
import {
  ProcesaEnvioSimpleApiResponse,
  ProcesaEnvioSimpleRequest
} from '../models/envio-simple-procesa-envio-simple.model';
import { EnvioSimpleFlowService } from './envio-simple-flow.service';
import {
  mapProcesaEnvioSimpleRespuestaAConfirmacion,
  resolverArchivoDocumento
} from './envio-simple-procesamiento.mapper';

// --- Flujo legacy (ProcesaSubida): imports necesarios si se reactiva ---
// import { throwError, timer } from 'rxjs';
// import { filter, take, timeout } from 'rxjs/operators';
// import { GestionaDatosSubidaApiService } from 'src/app/pages/subidaPrincipal/services/gestiona-datos-subida-api.service';
// import { ResponseSubidaFicheros } from 'src/app/pages/subidaPrincipal/models/response-subida-ficheros';
// import {
//   construirRequestProcesamiento,
//   mapRespuestaApiAConfirmacion,
//   normalizarNombreFicheroSubida
// } from './envio-simple-procesamiento.mapper';
// const ID_TIPO_ENVIO_ENVIO_SIMPLE = 0;

export interface OpcionesProcesamientoEnvioSimple {
  isEnvioTest?: boolean;
  wait?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class EnvioSimpleProcesamientoApiService {
  constructor(
    private http: HttpClient,
    private loginService: LoginService,
    private endpointService: EndpointService,
    private conversionDatosApiService: ConversionDatosApiService,
    private documentoPdfApiService: DocumentoPdfConfiguracionApiService
    // Flujo legacy: private subidaApiService: GestionaDatosSubidaApiService
  ) {}

  /**
   * Sube PDFs temporales si faltan, mapea el estado del wizard y llama a
   * `POST api/Envios/ProcesaEnvioSimple`.
   */
  procesar(
    estado: ReturnType<EnvioSimpleFlowService['exportarEstadoFlujo']>,
    opciones: OpcionesProcesamientoEnvioSimple = {}
  ): Observable<EnvioSimpleConfirmacionResultado> {
    const isEnvioTest = opciones.isEnvioTest ?? false;
    const wait = opciones.wait ?? true;

    return from(this.prepararFicherosTemporales(estado)).pipe(
      switchMap(estadoPreparado => {
        const request = this.conversionDatosApiService.mapProcesaEnvioSimpleSalida(estadoPreparado, {
          isEnvioTest,
          wait
        });
        // eslint-disable-next-line no-console
        console.log('[ENVIO-SIMPLE][ProcesaEnvioSimple][request]', request);
        return this.postProcesaEnvioSimple(request).pipe(
          map(response =>
            mapProcesaEnvioSimpleRespuestaAConfirmacion(response, estadoPreparado.firmantesEnvio)
          )
        );
      }),
      catchError(error => {
        const mensaje =
          error?.error?.ExceptionMessage ??
          error?.error?.mensajeError ??
          error?.message ??
          'No se ha podido procesar el envío.';
        return of(
          mapProcesaEnvioSimpleRespuestaAConfirmacion(
            { Ok: false, ExceptionMessage: mensaje },
            estado.firmantesEnvio
          )
        );
      })
    );
  }

  private postProcesaEnvioSimple(request: ProcesaEnvioSimpleRequest): Observable<ProcesaEnvioSimpleApiResponse> {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        authorization: 'Bearer ' + this.loginService.getUserLocalStorage().Token.Token
      })
    };

    return this.http.post<ProcesaEnvioSimpleApiResponse>(this.resolveProcesaEnvioSimpleUrl(), request, options);
  }

  private resolveProcesaEnvioSimpleUrl(): string {
    try {
      return this.endpointService.UrlProcesaEnvioSimple;
    } catch {
      const config = EndpointConfigV3.find(endpoint => endpoint.nombre === 'ProcesaEnvioSimple');
      const path = config?.endpointV3 ?? 'api/Envios/ProcesaEnvioSimple';
      return `${environment.ApiUrlV3}${path}`;
    }
  }

  private async prepararFicherosTemporales(
    estado: ReturnType<EnvioSimpleFlowService['exportarEstadoFlujo']>
  ): Promise<ReturnType<EnvioSimpleFlowService['exportarEstadoFlujo']>> {
    const documentosOrdenados = [...estado.documentos].sort(
      (a, b) => a.documento.orden - b.documento.orden
    );

    for (const item of documentosOrdenados) {
      const idPdfActual = (item.formularioDocumento.get('idPdf')?.value as string | undefined)?.trim();
      if (idPdfActual) {
        continue;
      }

      const archivo = resolverArchivoDocumento(item.documento);
      const idFicheroTemporal = await this.subirFicheroTemporal(archivo);
      item.formularioDocumento.get('idPdf')?.setValue(idFicheroTemporal, { emitEvent: false });
    }

    return estado;
  }

  private async subirFicheroTemporal(file: File): Promise<string> {
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);
    const id = await this.documentoPdfApiService.putDocumentoTemporal(dataTransfer.files).toPromise();
    if (!id) {
      throw new Error(`No se ha podido subir el fichero temporal "${file.name}".`);
    }
    return id;
  }

  // ===========================================================================
  // FLUJO LEGACY: CreaSubida + AddFicheroSubida + ProcesaSubida + GetLogSubida
  // Descomentar imports/constructor/procesar y comentar el flujo ProcesaEnvioSimple
  // si se necesita volver al envio por subida clasica (idTipoEnvio en payload).
  // ===========================================================================
  //
  // procesar(
  //   estado: ReturnType<EnvioSimpleFlowService['exportarEstadoFlujo']>,
  //   opciones: OpcionesProcesamientoEnvioSimple = {}
  // ): Observable<EnvioSimpleConfirmacionResultado> {
  //   const isEnvioTest = opciones.isEnvioTest ?? false;
  //   const wait = opciones.wait ?? true;
  //
  //   return this.crearSubidaYSubirDocumentos(estado).pipe(
  //     switchMap(idSubida => {
  //       const request = construirRequestProcesamiento(
  //         estado,
  //         idSubida,
  //         ID_TIPO_ENVIO_ENVIO_SIMPLE,
  //         isEnvioTest,
  //         wait
  //       );
  //       return this.subidaApiService.procesaFicheros(idSubida, request).pipe(
  //         switchMap(() => (wait ? this.esperarLogSubida(idSubida) : of(null))),
  //         map(log =>
  //           mapRespuestaApiAConfirmacion(
  //             {
  //               Ok: true,
  //               Data: {
  //                 log: log ?? undefined,
  //                 finalizado: log?.subida?.finalizado ?? !wait
  //               }
  //             },
  //             estado.firmantesEnvio
  //           )
  //         )
  //       );
  //     }),
  //     catchError(error => {
  //       const mensaje =
  //         error?.error?.ExceptionMessage ??
  //         error?.error?.mensajeError ??
  //         error?.message ??
  //         'No se ha podido procesar el envio.';
  //       return of(mapRespuestaApiAConfirmacion({ Ok: false, ExceptionMessage: mensaje }, estado.firmantesEnvio));
  //     })
  //   );
  // }
  //
  // private crearSubidaYSubirDocumentos(
  //   estado: ReturnType<EnvioSimpleFlowService['exportarEstadoFlujo']>
  // ): Observable<number> {
  //   return this.subidaApiService.getIdSubida().pipe(
  //     switchMap((response: { Data: number }) => {
  //       const idSubida = Number(response?.Data);
  //       if (!idSubida) {
  //         return throwError(() => new Error('No se ha podido crear la subida de ficheros.'));
  //       }
  //       const documentos = [...estado.documentos]
  //         .sort((a, b) => a.documento.orden - b.documento.orden)
  //         .map(item => item.documento);
  //
  //       return from(this.subirDocumentosSecuencial(idSubida, documentos)).pipe(map(() => idSubida));
  //     })
  //   );
  // }
  //
  // private async subirDocumentosSecuencial(
  //   idSubida: number,
  //   documentos: ReturnType<EnvioSimpleFlowService['exportarEstadoFlujo']>['documentos'][number]['documento'][]
  // ): Promise<void> {
  //   for (const documento of documentos) {
  //     const archivo = resolverArchivoDocumento(documento);
  //     const formData = new FormData();
  //     formData.append('idTipoEnvio', ID_TIPO_ENVIO_ENVIO_SIMPLE.toString());
  //     formData.append('file', archivo, normalizarNombreFicheroSubida(archivo.name));
  //     await this.subidaApiService.SubeFichero(formData, idSubida).toPromise();
  //   }
  // }
  //
  // private esperarLogSubida(idSubida: number): Observable<ResponseSubidaFicheros> {
  //   return timer(0, 2000).pipe(
  //     switchMap(() => this.subidaApiService.getLogSubida(idSubida)),
  //     map(log => log as unknown as ResponseSubidaFicheros),
  //     filter(log => !!log?.subida?.finalizado),
  //     take(1),
  //     timeout(120000)
  //   );
  // }
}
